import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center px-4">
      <div className="glass-solid max-w-md w-full p-8 rounded-2xl border border-white/10" data-testid="reset-password-card">
        <div className="text-[#00E0FF] text-xs font-bold tracking-[2px] mb-2">TRADEINSIGHTS AI</div>
        <h1 className="text-2xl font-bold text-white mb-6" style={{ fontFamily: 'Unbounded' }}>
          {done ? "PASSWORD UPDATED" : "SET NEW PASSWORD"}
        </h1>

        {done ? (
          <div className="space-y-5">
            <div className="flex items-center gap-3 text-[#00E0FF]">
              <CheckCircle2 className="w-6 h-6" />
              <p className="text-gray-300">Your password has been updated. Sign in with your new password.</p>
            </div>
            <Button
              data-testid="reset-go-dashboard-btn"
              onClick={() => navigate("/dashboard")}
              className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold py-5"
            >
              Go to Dashboard
            </Button>
          </div>
        ) : !token ? (
          <p className="text-gray-400" data-testid="reset-invalid-msg">
            This reset link is invalid. Request a new one from the sign-in screen.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              data-testid="reset-password-input"
              type="password"
              placeholder="New password (min 8 characters)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E0FF]/50"
            />
            <Input
              data-testid="reset-confirm-input"
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={8}
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#00E0FF]/50"
            />
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <Button
              data-testid="reset-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#00E0FF] text-black hover:bg-[#33E9FF] font-semibold py-5"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Update Password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
